import type { BannerConfig, BannerTexts, CookieCategory, StorageType } from '@ilc/shared';

/**
 * Basisadresse der API.
 *
 * Leer bedeutet: gleicher Ursprung wie das Dashboard. In der Produktion läuft
 * die API ueber eine Weiterleitung unter /api, damit das Session-Cookie ein
 * Erstanbieter-Cookie bleibt. Lokal zeigt die Variable direkt auf wrangler dev.
 *
 * Der Rueckfallwert haengt deshalb an import.meta.env.PROD und nicht an einer
 * festen Adresse. Ein Build ohne gesetztes VITE_API_URL hatte sonst
 * 127.0.0.1:8787 fest eingebacken: die veroeffentlichte Oberflaeche sprach
 * dann den Rechner des Besuchers an, Chrome fragte nach Zugriff auf das
 * lokale Netz, und die Anmeldung konnte gar nicht gelingen. Passiert am
 * 2026-10-18 bei einem Deploy von Hand.
 */
const BASE = (
  import.meta.env.VITE_API_URL ?? (import.meta.env.PROD ? '' : 'http://127.0.0.1:8787')
).replace(/\/$/, '');

export class ApiError extends Error {
  constructor(
    public status: number,
    public code: string,
    message: string,
    /** Zusatzangaben des Servers, z. B. die Kennung eines bereits vorhandenen Eintrags. */
    public siteId?: string,
  ) {
    super(message);
  }
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const response = await fetch(BASE + path, {
    ...init,
    // Session-Cookie mitschicken, sonst ist jede Anfrage anonym.
    credentials: 'include',
    headers: {
      ...(init.body ? { 'Content-Type': 'application/json' } : {}),
      ...init.headers,
    },
  });

  if (response.status === 204) return undefined as T;

  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    const error = (data as { error?: { code?: string; message?: string; siteId?: string } }).error;
    throw new ApiError(
      response.status,
      error?.code ?? 'unknown',
      error?.message ?? 'Anfrage fehlgeschlagen.',
      error?.siteId,
    );
  }
  return data as T;
}

// ---------------------------------------------------------------- Typen

export interface Me {
  user: { id: string; email: string; name: string | null; platformRole: string; locale: string };
  organizations: Organization[];
}

export interface Organization {
  id: string;
  name: string;
  slug: string;
  plan: string;
  siteLimit: number;
  scanPageLimit: number;
  managed: boolean;
}

export interface SiteSummary {
  id: string;
  organizationId: string;
  organizationName: string;
  name: string;
  domain: string;
  publicId: string;
  status: string;
  lastScanAt: string | null;
  consentVersion: number;
  createdAt: string;
}

export interface SiteDetail extends SiteSummary {
  additionalDomains: string[];
  consentLifetimeDays: number;
  defaultLanguage: string;
  googleConsentMode: boolean;
  googleConsentModeAdvanced: boolean;
  scanIntervalDays: number;
  consentRetentionMonths: number;
  setupSteps: Record<string, string>;
  bannerConfig: BannerConfig;
  texts: Record<string, BannerTexts>;
  blockRules: Array<{ pattern: string; category: CookieCategory }>;
  /** Tarif dieser Domain. null bedeutet: der des Mandanten gilt. */
  plan: string | null;
  /** Zuletzt gemessene Zahl eigener Unterseiten. */
  subpageCount: number | null;
}

export interface CookieRow {
  id: string;
  name: string;
  domain: string;
  type: StorageType;
  category: CookieCategory;
  purpose: string;
  vendor: string | null;
  maxAgeSeconds: number | null;
  thirdParty: boolean;
  classificationSource: string;
  confidence: number;
  reviewed: boolean;
  visible: boolean;
  lastSeenAt: string;
}

export interface ScanRow {
  id: string;
  status: string;
  startUrl: string;
  pagesScanned: string[];
  foundCount: number;
  newCount: number;
  unclassifiedCount: number;
  error: string | null;
  startedAt: string;
  finishedAt: string | null;
}

/** Ergebnis der Einbindungspruefung auf der Kundenwebsite. */
export interface EmbedCheck {
  erreichbar: boolean;
  url: string;
  fehler?: string;
  geprueftAm?: string;
  skriptZahl?: number;
  einbindung?: { vorhanden: boolean; fremdeKennung: string[]; imKopf: boolean; zuerst: boolean; davorImKopf: string[] };
  fremdeCmp?: Array<{ name: string; src: string; selbstBlockiert: boolean }>;
  ungeblockteTracker?: Array<{ name: string; src: string; empfohleneKategorie: string }>;
  markierteSkripte?: Array<{ src: string; kategorie: string }>;
  einbettungen?: Array<{ name: string; src: string; kategorie: string; ersatz: string }>;
  hatOpenLink?: boolean;
  hatErklaerung?: boolean;
  erklaerungAufSeite?: string | null;
  erklaerungSeiteHatLoader?: boolean;
  seiten?: Array<{ url: string; status: number; loader: boolean; footerLink: boolean; erklaerung: boolean }>;
  seitenAbgeschnitten?: boolean;
}

export interface StatsResponse {
  days: Array<{
    day: string;
    total: number;
    acceptAll: number;
    acceptSelection: number;
    rejectAll: number;
    statisticsGranted: number;
    marketingGranted: number;
  }>;
  totals: {
    total: number;
    acceptAll: number;
    acceptSelection: number;
    rejectAll: number;
    statisticsGranted: number;
    marketingGranted: number;
  };
}

export interface Rechnungsposten {
  siteId: string | null;
  bezeichnung: string;
  betragCents: number;
}

export interface Billing {
  /** false bedeutet: kein Stripe-Schlüssel gesetzt, Abrechnung ruht. */
  eingerichtet: boolean;
  zahlungsmittelHinterlegt: boolean;
  problem: string | null;
  vorschau: {
    monat: string;
    posten: Rechnungsposten[];
    summeCents: number;
    steuerhinweis: string;
    /** true = eigener Mandant, wird nicht abgerechnet. */
    ausgenommen?: boolean;
  };
  rechnungen: Array<{ period: string; amountCents: number; status: string; lines: Rechnungsposten[] }>;
}

// ---------------------------------------------------------------- Endpunkte

export const api = {
  me: () => request<Me>('/api/auth/me'),
  login: (email: string, password: string) =>
    request<{ user: Me['user'] }>('/api/auth/login', { method: 'POST', body: JSON.stringify({ email, password }) }),
  bootstrap: (payload: { email: string; password: string; name?: string; organization?: string }) =>
    request<{ user: Me['user'] }>('/api/auth/bootstrap', { method: 'POST', body: JSON.stringify(payload) }),
  logout: () => request<void>('/api/auth/logout', { method: 'POST' }),
  changePassword: (currentPassword: string, newPassword: string) =>
    request<{ ok: true; beendeteSitzungen: number }>('/api/auth/password', {
      method: 'POST',
      body: JSON.stringify({ currentPassword, newPassword }),
    }),

  organizations: () => request<{ organizations: Organization[] }>('/api/organizations'),
  createOrganization: (payload: { name: string; plan?: string; managed?: boolean }) =>
    request<{ organization: Organization }>('/api/organizations', { method: 'POST', body: JSON.stringify(payload) }),

  billing: (orgId: string) => request<Billing>(`/api/organizations/${orgId}/billing`),
  billingSetup: (orgId: string) =>
    request<{ url: string | null }>(`/api/organizations/${orgId}/billing/setup`, { method: 'POST' }),
  billingPortal: (orgId: string) =>
    request<{ url: string }>(`/api/organizations/${orgId}/billing/portal`, { method: 'POST' }),

  sites: () => request<{ sites: SiteSummary[] }>('/api/sites'),
  site: (id: string) =>
    request<{ site: SiteDetail; embedSnippet: string; scannerConfigured: boolean }>(`/api/sites/${id}`),
  createSite: (payload: { organizationId: string; name: string; domain: string }) =>
    request<{ site: SiteDetail }>('/api/sites', { method: 'POST', body: JSON.stringify(payload) }),
  updateSite: (id: string, payload: Record<string, unknown>) =>
    request<{ site: SiteDetail }>(`/api/sites/${id}`, { method: 'PATCH', body: JSON.stringify(payload) }),

  cookies: (siteId: string) => request<{ cookies: CookieRow[] }>(`/api/sites/${siteId}/cookies`),
  updateCookie: (cookieId: string, payload: Partial<Pick<CookieRow, 'category' | 'purpose' | 'vendor' | 'visible'>>) =>
    request<{ cookie: CookieRow }>(`/api/cookies/${cookieId}`, { method: 'PATCH', body: JSON.stringify(payload) }),

  scans: (siteId: string) => request<{ scans: ScanRow[] }>(`/api/sites/${siteId}/scans`),
  startScan: (siteId: string) => request<{ scan: ScanRow }>(`/api/sites/${siteId}/scans`, { method: 'POST' }),

  checkEmbed: (siteId: string) => request<EmbedCheck>(`/api/sites/${siteId}/check-embed`, { method: 'POST' }),

  stats: (siteId: string, days = 30) => request<StatsResponse>(`/api/sites/${siteId}/stats?days=${days}`),
};
